import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Palette, Globe, Image, Check, RefreshCw } from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Skeleton } from './components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME_FAMILIES = [
  { id: 'school', name: 'School', desc: 'Vibrant, approachable. Perfect for K-12 schools.', primary: '#0ea5e9', accent: '#10b981' },
  { id: 'institution', name: 'Institution', desc: 'Performance-focused. Ideal for coaching centers.', primary: '#3b82f6', accent: '#f97316' },
  { id: 'university', name: 'University', desc: 'Minimal, mature. Designed for higher education.', primary: '#1e3a5f', accent: '#14b8a6' },
];

export default function AdminBranding() {
  const { user, token } = useApp();
  const { applyTheme } = useApp();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/tenants/${user.tenant_id}/branding`, { headers })
      .then(r => { setBranding(r.data); applyTheme(r.data.theme_family || 'school'); })
      .catch(() => toast.error('Failed to load branding'))
      .finally(() => setLoading(false));
  }, [user?.tenant_id]);

  const save = async () => {
    setSaving(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.put(`${API}/tenants/${user.tenant_id}/branding`, branding, { headers });
      setBranding(res.data);
      applyTheme(res.data.theme_family);
      toast.success('Branding saved successfully');
    } catch { toast.error('Failed to save branding'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardShell title="Branding"><Skeleton className="h-96 rounded-2xl" /></DashboardShell>;

  return (
    <DashboardShell title="Branding & Theme" breadcrumb="Admin Dashboard">
      <div className="space-y-6 fade-in max-w-3xl">
        {/* Theme Family */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="w-4 h-4" style={{ color: 'hsl(var(--primary))' }} />
              Theme Family
            </CardTitle>
            <CardDescription>Choose the visual style that best matches your institution.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {THEME_FAMILIES.map(tf => (
                <button key={tf.id}
                  onClick={() => { setBranding(b => ({ ...b, theme_family: tf.id })); applyTheme(tf.id); }}
                  className="p-4 rounded-xl border-2 text-left transition-all duration-150"
                  style={{
                    borderColor: branding?.theme_family === tf.id ? tf.primary : 'hsl(var(--border))',
                    background: branding?.theme_family === tf.id ? `${tf.primary}08` : 'hsl(var(--surface))'
                  }}
                  data-testid={`theme-${tf.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{tf.name}</span>
                    {branding?.theme_family === tf.id && (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: tf.primary }}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 mb-2">
                    <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: tf.primary }} />
                    <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: tf.accent }} />
                  </div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{tf.desc}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Colors */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base">Color Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1.5 block">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding?.primary_color || '#0ea5e9'}
                    onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg border cursor-pointer" />
                  <Input value={branding?.primary_color || '#0ea5e9'}
                    onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))}
                    className="font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Accent Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding?.accent_color || '#10b981'}
                    onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg border cursor-pointer" />
                  <Input value={branding?.accent_color || '#10b981'}
                    onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))}
                    className="font-mono text-sm" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portal Text */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base">Portal Customization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Welcome Banner</Label>
              <Input value={branding?.welcome_banner || ''}
                onChange={e => setBranding(b => ({ ...b, welcome_banner: e.target.value }))}
                placeholder="Welcome to our learning portal!" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Footer Text</Label>
              <Input value={branding?.footer_text || ''}
                onChange={e => setBranding(b => ({ ...b, footer_text: e.target.value }))}
                placeholder="Your School Name. Powered by Desky." />
            </div>
          </CardContent>
        </Card>

        <Button onClick={save} disabled={saving} data-testid="save-branding-button"
          style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Branding'}
        </Button>
      </div>
    </DashboardShell>
  );
}
